<template>
  <div
    data-testid="upload-modal-step-preview-card"
    class="flex flex-col gap-y-4 py-2 h-full preview-step"
  >
    <div class="flex flex-col lg:flex-row gap-x-4">
      <div :class="mode === 'preview' ? 'w-full' : 'flex-1'" class="flex flex-col gap-y-2 min-w-0">
        <h3 v-if="showDisplayName" class="text-lg font-bold text-textPrimary text-left truncate">
          {{ displayName }}
        </h3>

        <div
          ref="previewContainer"
          data-testid="step-preview-viewport"
          class="relative border border-lightGray30 rounded-lg overflow-hidden bg-lightGray30 flex items-center justify-center w-full max-w-full"
          :class="heightPx ? '' : 'min-h-[500px] h-[650px] lg:min-w-[500px]'"
          :style="heightPx ? { height: `${heightPx}px`, minHeight: `${heightPx}px` } : undefined"
        >
          <!--
            `hideToolbar`: quem embute o visualizador dentro de outro painel (o
            `AttachmentPanel.vue` do modal "Enviar registro") já oferece girar,
            ampliar e abrir em nova aba no PRÓPRIO cabeçalho. Sem isso, os dois
            jogos de botões apareceriam empilhados sobre a mesma folha.
          -->
          <div
            v-if="!hideToolbar"
            class="bg-[rgba(22,22,29,0.16)] p-1 absolute top-2 right-2 z-10 flex flex-col rounded-md shadow-sm overflow-hidden gap-1"
          >
            <v-tooltip text="Expandir" location="left">
              <template #activator="{ props: tooltipProps }">
                <div class="border border-primary30 rounded-md">
                  <button
                    v-bind="tooltipProps"
                    @click="toggleFullscreen"
                    class="bg-white w-10 h-8 text-primary30 rounded-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <v-icon :icon="mdiFullscreen" size="18" />
                  </button>
                </div>
              </template>
            </v-tooltip>

            <v-tooltip text="Aumentar Zoom" location="left">
              <template #activator="{ props: tooltipProps }">
                <div class="border border-primary30 rounded-md">
                  <button
                    v-bind="tooltipProps"
                    @click="zoomIn"
                    class="bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <v-icon :icon="mdiPlus" size="18" />
                  </button>
                </div>
              </template>
            </v-tooltip>

            <v-tooltip text="Diminuir Zoom" location="left">
              <template #activator="{ props: tooltipProps }">
                <div class="border border-primary30 rounded-md">
                  <button
                    v-bind="tooltipProps"
                    @click="zoomOut"
                    class="bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <v-icon :icon="mdiMinus" size="18" />
                  </button>
                </div>
              </template>
            </v-tooltip>

            <template v-if="mode === 'preview'">
              <v-tooltip text="Rotacionar imagem" location="left">
                <template #activator="{ props: tooltipProps }">
                  <div class="border border-primary30 rounded-md">
                    <button
                      v-bind="tooltipProps"
                      @click="rotateImage"
                      class="bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                    >
                      <v-icon :icon="mdiRotateRight" size="18" />
                    </button>
                  </div>
                </template>
              </v-tooltip>

              <!--
                Lupa 2× na própria barra do visualizador (pedido do PO, 2026-09-12): quem
                confere assinatura não larga a folha para caçar um botão no cabeçalho. É um
                botão de ESTADO — `aria-pressed` e cores invertidas quando ligada — e ele só
                aparece para quem pede (`magnifierToggle`), então o modal de upload segue igual.
              -->
              <v-tooltip v-if="magnifierToggle" :text="magnifierTooltip" location="left">
                <template #activator="{ props: tooltipProps }">
                  <div class="border border-primary30 rounded-md">
                    <button
                      v-bind="tooltipProps"
                      type="button"
                      data-testid="ds-attendance-magnifier-toggle"
                      :aria-pressed="magnifier === true"
                      :aria-label="magnifierTooltip"
                      :title="magnifierTooltip"
                      :disabled="isMagnifierBlocked"
                      class="rounded-md w-10 h-8 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      :class="
                        magnifier === true
                          ? 'bg-primary30 text-white'
                          : 'bg-white text-primary30 hover:bg-gray-50'
                      "
                      @click="toggleMagnifier"
                    >
                      <v-icon :icon="mdiMagnify" size="18" />
                    </button>
                  </div>
                </template>
              </v-tooltip>

              <v-tooltip text="Abrir" location="left">
                <template #activator="{ props: tooltipProps }">
                  <div class="border border-primary30 rounded-md">
                    <a
                      v-bind="tooltipProps"
                      :href="previewUrl ?? undefined"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="bg-white rounded-md w-10 h-8 text-primary30 flex items-center justify-center hover:bg-gray-50 transition-colors"
                    >
                      <v-icon :icon="mdiOpenInNew" size="18" />
                    </a>
                  </div>
                </template>
              </v-tooltip>
            </template>
          </div>

          <div
            ref="scrollContainer"
            class="w-full h-full flex items-center justify-center select-none"
            :class="[
              isPdf ? 'overflow-visible' : 'overflow-hidden',
              !isPdf && zoom > 1 ? 'cursor-grab active:cursor-grabbing' : ''
            ]"
            @mousedown="startDragging"
            @mousemove="handleMouseMove"
            @mouseup="stopDragging"
            @mouseleave="handleMouseLeave"
          >
            <img
              ref="imageRef"
              v-if="previewUrl && !isPdf"
              :src="previewUrl"
              :class="[
                'shadow-lg pointer-events-none ease-out',
                isDragging ? 'transition-none' : 'transition-transform duration-300'
              ]"
              :style="{
                transform: `translate(${panX}px, ${panY}px) scale(${zoom}) rotate(${rotation}deg)`,
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }"
              @error="emit('image-error')"
            />
            <PdfRenderer
              v-else-if="previewUrl && isPdf"
              :src="previewUrl"
              :scale="zoom"
              :rotation="rotation"
              :page="currentPage"
              @update:page="setPage($event)"
              @loaded="
                ({ totalPages: tp }) => {
                  totalPages = tp
                  pdfLoadError = false
                  emit('update:totalPages', tp)
                  refreshMagnifierSource()
                }
              "
              @error="pdfLoadError = true"
            />
          </div>

          <!--
            Lupa 2x (cara-cracha V1 + lupa): lente que segue o mouse sobre a folha. A fonte
            e o `background-image` da lente: para imagem, a propria URL; para PDF, o
            snapshot do canvas renderizado, capturado uma vez por pagina/zoom/rotacao (nunca
            a cada `mousemove`). Com a folha rotacionada a lente nao aparece: a geometria
            aponta para a regiao errada, e a tela de auditoria desabilita o botao.
          -->
          <div
            v-if="isMagnifierActive && lens.visible && magnifierSource"
            data-testid="step-preview-magnifier-lens"
            aria-hidden="true"
            class="pointer-events-none absolute z-20 rounded-md border-2 border-primary30 shadow-lg bg-white bg-no-repeat"
            :style="{
              left: `${lens.left}px`,
              top: `${lens.top}px`,
              width: `${MAGNIFIER_WIDTH}px`,
              height: `${MAGNIFIER_HEIGHT}px`,
              backgroundImage: `url(${magnifierSource})`,
              backgroundSize: `${lens.bgWidth}px ${lens.bgHeight}px`,
              backgroundPosition: `${lens.bgPosX}px ${lens.bgPosY}px`
            }"
          />
        </div>
      </div>

      <div v-if="mode !== 'preview'" class="w-full lg:w-[360px] flex flex-col text-left">
        <div>
          <h3 class="text-lg font-bold text-textPrimary mb-4">Confirme a leitura de arquivo</h3>
          <p class="text-base text-neutral50 leading-relaxed mb-4">
            Certifique-se de que é o arquivo correto e está rotacionado na orientação correta para
            leitura.
          </p>
          <p class="text-base text-neutral50 leading-relaxed">
            Caso necessário, use o botão abaixo para rotacionar a imagem.
          </p>
        </div>

        <outlined-button
          text="Rotacionar imagem"
          @click="rotateImage"
          height="32px"
          width="full"
          class="mt-4"
          :prepend-icon="mdiRotateRight"
        />

        <div v-if="monitoringTypeLabel" class="flex flex-col gap-1 text-left mt-6">
          <span class="text-base font-bold leading-6 tracking-[0.08px] text-[#16161D]">
            Modalidade de registro
          </span>
          <span class="text-sm text-textPrimary">{{ monitoringTypeLabel }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted, nextTick, defineAsyncComponent } from 'vue'
import { mdiFullscreen, mdiRotateRight, mdiPlus, mdiMinus, mdiOpenInNew, mdiMagnify } from '@mdi/js'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  MAGNIFIER_HEIGHT,
  MAGNIFIER_ROTATION_TOOLTIP,
  MAGNIFIER_WIDTH,
  computeMagnifierGeometry,
  isMagnifierBlockedByRotation as magnifierBlockedByRotation
} from '@/modules/company/Approvals/DsAttendance/utils/sheet-magnifier'

const PdfRenderer = defineAsyncComponent(() => import('./PdfRenderer.vue'))

const props = withDefaults(
  defineProps<{
    file: File | null
    fileUrl?: string
    fileType?: string
    rotation: number
    zoom: number
    mode?: 'upload' | 'preview'
    showDisplayName?: boolean
    monitoringTypeLabel?: string
    /**
     * Cara-crachá V1 — página controlada de fora (a grade pula a folha para a página da
     * linha). Ausente, a paginação segue interna, como antes.
     */
    page?: number
    /** Lupa 2× ligada. Desligada por padrão: quem liga é a tela de auditoria. */
    magnifier?: boolean
    /**
     * Mostra o botão da lupa na barra de ícones do visualizador. Só a tela de auditoria pede:
     * no modal de upload a lupa não tem função e a barra fica como sempre foi.
     */
    magnifierToggle?: boolean
    magnifierZoom?: number
    /** Altura fixa em px (layout V1 usa altura arrastável); ausente, mantém 650px/500 min. */
    heightPx?: number
    /** Esconde a barra de ícones interna: quem embute o visualizador já tem a sua. */
    hideToolbar?: boolean
  }>(),
  { showDisplayName: true, monitoringTypeLabel: '' }
)

const isPdf = computed(() => {
  if (props.file) return props.file.type === 'application/pdf'
  if (props.fileType) return props.fileType === 'application/pdf'
  if (props.fileUrl) return props.fileUrl.toLowerCase().includes('.pdf')
  return false
})

const displayName = computed(() => {
  if (props.file) return props.file.name
  if (props.fileUrl) {
    const parts = props.fileUrl.split('/')
    return parts[parts.length - 1] || props.fileUrl
  }
  return ''
})

/**
 * `update:totalPages` existe para o checklist do Eixo 2A: `expectedPages` é quantas páginas o
 * emissor anexou, e só este componente sabe quantas o PDF tem (imagem é sempre 1).
 */
const emit = defineEmits<{
  'update:rotation': [rotation: number]
  'update:zoom': [zoom: number]
  /** Paginação do PDF para fora — a grade destaca as linhas da página corrente. */
  'update:page': [page: number]
  'update:totalPages': [totalPages: number]
  /** Clique no botão da lupa: o valor pedido, para quem controla o estado aplicar. */
  'update:magnifier': [on: boolean]
  /**
   * A `<img>` falhou ao carregar (URL revogada, blob corrompido, tipo que o browser
   * não decodifica). Quem monta decide o fallback — este componente nunca mostra uma
   * imagem quebrada sozinho.
   */
  'image-error': []
}>()

const previewUrl = ref<string | null>(null)
const previewContainer = ref<HTMLElement | null>(null)
const scrollContainer = ref<HTMLElement | null>(null)
const imageRef = ref<HTMLImageElement | null>(null)

const currentPage = ref(1)
const totalPages = ref(1)
const pdfLoadError = ref(false)

watch(totalPages, (value) => emit('update:totalPages', value), { immediate: true })

/**
 * Página controlada de fora quando a prop vem definida: a tela de auditoria manda a página
 * da linha clicada, e o `update:page` do renderizador devolve a navegação pelas setas.
 */
watch(
  () => props.page,
  (page) => {
    if (typeof page === 'number' && page >= 1 && page !== currentPage.value)
      currentPage.value = page
  },
  { immediate: true }
)

const setPage = (page: number) => {
  currentPage.value = page
  emit('update:page', page)
  refreshMagnifierSource()
}

// --- Lupa 2× (cara-crachá V1 + lupa) -------------------------------------------------
/**
 * A lupa só opera ligada E sem rotação — com a folha rotacionada a geometria da lente aponta
 * para outra região (regra compartilhada com a tela de auditoria, que desabilita o botão).
 */
const isMagnifierActive = computed(
  () => props.magnifier === true && !magnifierBlockedByRotation(props.rotation)
)

/** Mesma regra do `isMagnifierActive`, isolada porque o botão precisa dela sem o "ligada". */
const isMagnifierBlocked = computed(() => magnifierBlockedByRotation(props.rotation))

const magnifierTooltip = computed(() => {
  if (isMagnifierBlocked.value) return MAGNIFIER_ROTATION_TOOLTIP
  return props.magnifier === true ? 'Desligar lupa (atalho L)' : 'Lupa 2× (atalho L)'
})

function toggleMagnifier(): void {
  if (isMagnifierBlocked.value) return
  emit('update:magnifier', props.magnifier !== true)
}

/** URL/data-URL do que a lente amplia; `null` enquanto não há fonte utilizável. */
const magnifierSource = ref<string | null>(null)
const lens = ref({
  visible: false,
  left: 0,
  top: 0,
  bgWidth: 0,
  bgHeight: 0,
  bgPosX: 0,
  bgPosY: 0
})

/**
 * PDF renderiza num canvas — a lente precisa de um bitmap. `toDataURL` lança em canvas
 * "tainted" (folha servida sem CORS): nesse caso a lupa simplesmente não acende, em vez de
 * derrubar a tela.
 */
function refreshMagnifierSource(): void {
  if (!isMagnifierActive.value) return
  if (!isPdf.value) {
    magnifierSource.value = previewUrl.value
    return
  }
  nextTick(() => {
    requestAnimationFrame(() => {
      const canvas = scrollContainer.value?.querySelector('canvas') ?? null
      if (!canvas) {
        magnifierSource.value = null
        return
      }
      try {
        magnifierSource.value = canvas.toDataURL('image/png')
      } catch (error) {
        console.warn('Lupa indisponível para esta folha (canvas sem permissão de leitura).', error)
        magnifierSource.value = null
      }
    })
  })
}

/** Retângulo da folha renderizada — a imagem quando há imagem, o canvas quando é PDF. */
function magnifierTarget(): HTMLElement | null {
  if (!isPdf.value) return imageRef.value
  return (scrollContainer.value?.querySelector('canvas') as HTMLElement | null) ?? null
}

function updateLens(point: { clientX: number; clientY: number }): void {
  const target = magnifierTarget()
  if (!target) {
    lens.value = { ...lens.value, visible: false }
    return
  }
  const rect = target.getBoundingClientRect()
  const geometry = computeMagnifierGeometry({
    mouseX: point.clientX,
    mouseY: point.clientY,
    rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
    zoom: props.magnifierZoom
  })
  const container = scrollContainer.value?.getBoundingClientRect()
  // A lente é filha do viewport, não do canvas: o offset do canvas dentro dele entra aqui.
  const offsetX = container ? rect.left - container.left : 0
  const offsetY = container ? rect.top - container.top : 0
  lens.value = {
    visible: geometry.visible,
    left: geometry.lensLeft + offsetX,
    top: geometry.lensTop + offsetY,
    bgWidth: geometry.bgWidth,
    bgHeight: geometry.bgHeight,
    bgPosX: geometry.bgPosX,
    bgPosY: geometry.bgPosY
  }
}

/**
 * `mousemove` dispara dezenas de vezes por segundo e cada passada faz um
 * `getBoundingClientRect` + escrita reativa que repinta a lente. Coalescer por frame deixa no
 * máximo uma atualização por quadro — é o que o olho vê de qualquer forma.
 */
let pendingLensPoint: { clientX: number; clientY: number } | null = null
let lensFrame: number | null = null

function cancelLensFrame(): void {
  if (lensFrame !== null) cancelAnimationFrame(lensFrame)
  lensFrame = null
  pendingLensPoint = null
}

function scheduleLensUpdate(event: MouseEvent): void {
  pendingLensPoint = { clientX: event.clientX, clientY: event.clientY }
  if (lensFrame !== null) return
  lensFrame = requestAnimationFrame(() => {
    lensFrame = null
    const point = pendingLensPoint
    pendingLensPoint = null
    if (point) updateLens(point)
  })
}

function handleMouseMove(event: MouseEvent): void {
  doDragging(event)
  if (isMagnifierActive.value) {
    if (!magnifierSource.value) refreshMagnifierSource()
    scheduleLensUpdate(event)
  }
}

function handleMouseLeave(): void {
  stopDragging()
  cancelLensFrame()
  lens.value = { ...lens.value, visible: false }
}

watch(isMagnifierActive, (on) => {
  if (on) {
    refreshMagnifierSource()
    return
  }
  // Desligada, a lupa não guarda o snapshot da folha: é um data-URL de página inteira.
  cancelLensFrame()
  magnifierSource.value = null
  lens.value = { ...lens.value, visible: false }
})

// Zoom e rotação re-renderizam o canvas: o snapshot da lente precisa acompanhar.
watch([() => props.zoom, () => props.rotation], () => refreshMagnifierSource())

const isDragging = ref(false)
const startX = ref(0)
const startY = ref(0)
const startPanX = ref(0)
const startPanY = ref(0)
const panX = ref(0)
const panY = ref(0)

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max)

const calculateBounds = () => {
  if (!scrollContainer.value || !imageRef.value) return { maxPanX: 0, maxPanY: 0 }

  const img = imageRef.value
  const container = scrollContainer.value

  const isRotated = props.rotation % 180 !== 0
  const imgNaturalWidth = isRotated ? img.naturalHeight : img.naturalWidth
  const imgNaturalHeight = isRotated ? img.naturalWidth : img.naturalHeight

  if (!imgNaturalWidth || !imgNaturalHeight) return { maxPanX: 0, maxPanY: 0 }

  const containerWidth = container.clientWidth
  const containerHeight = container.clientHeight

  const imgRatio = imgNaturalWidth / imgNaturalHeight
  const containerRatio = containerWidth / containerHeight

  let renderedWidth = 0
  let renderedHeight = 0

  if (imgRatio > containerRatio) {
    renderedWidth = containerWidth
    renderedHeight = containerWidth / imgRatio
  } else {
    renderedHeight = containerHeight
    renderedWidth = containerHeight * imgRatio
  }

  const scaledWidth = renderedWidth * props.zoom
  const scaledHeight = renderedHeight * props.zoom

  const maxPanX = Math.max(0, (scaledWidth - containerWidth) / 2)
  const maxPanY = Math.max(0, (scaledHeight - containerHeight) / 2)

  return { maxPanX, maxPanY }
}

const enforceBounds = () => {
  const { maxPanX, maxPanY } = calculateBounds()
  panX.value = clamp(panX.value, -maxPanX, maxPanX)
  panY.value = clamp(panY.value, -maxPanY, maxPanY)
}

const objectUrl = ref<string | null>(null)

watch(
  [() => props.file, () => props.fileUrl],
  ([newFile, newFileUrl]) => {
    currentPage.value = 1
    totalPages.value = 1
    pdfLoadError.value = false
    magnifierSource.value = null

    if (objectUrl.value) {
      URL.revokeObjectURL(objectUrl.value)
      objectUrl.value = null
    }
    if (newFileUrl) {
      previewUrl.value = newFileUrl
    } else if (newFile) {
      const url = URL.createObjectURL(newFile)
      objectUrl.value = url
      previewUrl.value = url
    } else {
      previewUrl.value = null
    }
  },
  { immediate: true }
)

watch(
  () => props.zoom,
  (newZoom) => {
    if (newZoom <= 1) {
      panX.value = 0
      panY.value = 0
    } else {
      nextTick(() => enforceBounds())
    }
  }
)

watch(
  () => props.rotation,
  () => {
    if (props.zoom > 1) {
      nextTick(() => enforceBounds())
    }
  }
)

onUnmounted(() => {
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value)
  cancelLensFrame()
  magnifierSource.value = null
})

const startDragging = (e: MouseEvent) => {
  if (isPdf.value || props.zoom <= 1) return
  isDragging.value = true
  startX.value = e.clientX
  startY.value = e.clientY
  startPanX.value = panX.value
  startPanY.value = panY.value
}

const stopDragging = () => {
  isDragging.value = false
}

const doDragging = (e: MouseEvent) => {
  if (!isDragging.value) return
  e.preventDefault()

  const walkX = e.clientX - startX.value
  const walkY = e.clientY - startY.value

  const nextPanX = startPanX.value + walkX
  const nextPanY = startPanY.value + walkY

  const { maxPanX, maxPanY } = calculateBounds()

  panX.value = clamp(nextPanX, -maxPanX, maxPanX)
  panY.value = clamp(nextPanY, -maxPanY, maxPanY)
}

const rotateImage = () => {
  emit('update:rotation', (props.rotation + 90) % 360)
}

const zoomIn = () => {
  if (props.zoom < 4) {
    let newZoom = props.zoom + 0.2
    newZoom = Math.min(4, Math.round(newZoom * 10) / 10)
    emit('update:zoom', newZoom)
  }
}

const zoomOut = () => {
  let newZoom = props.zoom
  if (newZoom > 1) {
    newZoom -= 0.2
  } else if (newZoom > 0.4) {
    newZoom -= 0.1
  }

  newZoom = Math.max(0.4, Math.round(newZoom * 10) / 10)
  emit('update:zoom', newZoom)
}

const toggleFullscreen = () => {
  if (!previewContainer.value) return
  if (!document.fullscreenElement) {
    previewContainer.value.requestFullscreen().catch((err) => {
      console.error(`Erro ao tentar ativar modo tela cheia: ${err.message}`)
    })
  } else {
    document.exitFullscreen()
  }
}
</script>

<style scoped></style>
